import { EFinancingCompany } from '@if/core/types'
import { DateOnly } from '@if/core/date'
import { EAccountType, EProduct, SYSTEM_ACCOUNT_PRODUCT_ID } from '@src/types/constants'
import { reportErrorToConsole } from '@src/services/error-logger'
import { apiClient } from '@src/services/http-common'
import {
  CalendarSimulation,
  CalendarSimulationAction,
  ComputeCalendarSimulationAction,
  SignedCalendarSimulation,
  SpreadOverdueAmount,
} from '@src/types/loan/loan-simulation'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { useCallback } from 'react'
import { LoanLockDto, SignedCalendarSimulationDto, SpreadOverdueAmountResultDto } from './loan-dto'
import {
  transformCalendarActionToDto,
  transformComputeCalendarActionToDto as transformComputeCalendarSimulationActionToDto,
  transformSignedCalendarSimulationFromDto,
  transformSignedCalendarSimulationToDto,
  transformSpreadOverdueAmountToDto,
} from './loan-transformer'

const SCOPE = 'loans'

// QueryKey factory for consistent query key generation
export const loanQueryKeys = {
  all: [SCOPE] as const,
  loans: () => [...loanQueryKeys.all, 'loan'] as const,
  loan: (id: string) => [...loanQueryKeys.loans(), id] as const,
  loanCalendar: (id: string, financingCompanyId: EFinancingCompany) =>
    [...loanQueryKeys.loan(id), financingCompanyId, 'loanCalendar'] as const,
  computeAction: (id: string) => [...loanQueryKeys.loan(id), 'computeAction'] as const,
  applySimulation: (id: string) => [...loanQueryKeys.loan(id), 'applySimulation'] as const,
  refreshSimulation: (id: string) => [...loanQueryKeys.loan(id), 'refreshSimulation'] as const,
  computeCatchUpAmountOfOverdueForDays: (
    simulation: CalendarSimulation,
    startDate: DateOnly,
    endDate?: DateOnly | null,
  ) =>
    [
      ...loanQueryKeys.loan(simulation.fullLoan.loan.id),
      'computeCatchUpAmountOfOverdueForDays',
      simulation,
      startDate,
      endDate,
    ] as const,
  calculateAmountToSpreadOver: (
    id: string,
    simulation: SignedCalendarSimulation,
    numberOfAffectedPayment: number,
    fromPaymentDate: DateOnly,
  ) =>
    [
      ...loanQueryKeys.loan(id),
      'calculateAmountToSpreadOver',
      simulation,
      numberOfAffectedPayment,
      fromPaymentDate,
    ] as const,
  calculateNumberOfPaymentsToClearOverdueAmount: (
    id: string,
    financingCompanyId: EFinancingCompany,
    simulation: SignedCalendarSimulation,
    fromPaymentDate: DateOnly,
    amountPerPayment: number,
  ) =>
    [
      ...loanQueryKeys.loan(id),
      'calculateNumberOfPaymentsToClearOverdueAmount',
      financingCompanyId,
      simulation,
      fromPaymentDate,
      amountPerPayment,
    ] as const,
  loanLock: (id: string, financingCompanyId: EFinancingCompany) =>
    [...loanQueryKeys.loan(id), financingCompanyId, 'lock'] as const,
  acquireLock: (id: string) => [...loanQueryKeys.loan(id), 'acquireLock'] as const,
  releaseLock: (id: string) => [...loanQueryKeys.loan(id), 'releaseLock'] as const,
  replayMigrationEvents: (id: string) => [...loanQueryKeys.loan(id), 'replayMigrationEvents'] as const,
  generateValidationReport: (id: string) => [...loanQueryKeys.loan(id), 'generateValidationReport'] as const,
  journalTransactions: (id: string, financingCompanyId: EFinancingCompany) =>
    [...loanQueryKeys.loan(id), financingCompanyId, 'journalTransactions'] as const,
}

export interface JournalTransactionLineSummary {
  accountNumber: number
  /** A product, or SYSTEM_ACCOUNT_PRODUCT_ID (-1) when the line targets a system GL account. */
  productId: EProduct | typeof SYSTEM_ACCOUNT_PRODUCT_ID
  debit: number
  credit: number
  /** Role of the account for the product; null on system accounts and accounts the chart cannot classify. */
  accountType: EAccountType | null
}

export interface JournalTransactionSummary {
  id: number
  effectiveDate: string
  createdByName: string
  createdOn: string
  description: string
  amount: number
  periodId: number | null
  lines: JournalTransactionLineSummary[]
}

const getJournalTransactions = async (
  loanId: string,
  financingCompanyId: EFinancingCompany,
): Promise<JournalTransactionSummary[]> => {
  const response = await apiClient.get<JournalTransactionSummary[]>(
    `${financingCompanyId}/loans/${loanId}/journal-transactions`,
  )
  return response.data
}

export function useGetJournalTransactions(
  loanId: string,
  financingCompanyId: EFinancingCompany,
): [JournalTransactionSummary[] | undefined, boolean, Error | null] {
  const { data, isPending, error } = useQuery({
    queryKey: loanQueryKeys.journalTransactions(loanId, financingCompanyId),
    queryFn: () => getJournalTransactions(loanId, financingCompanyId),
    enabled: !!loanId,
  })
  return [data, isPending, error]
}

const getCalendarSimulation = async (
  id: string,
  financingCompanyId: EFinancingCompany,
): Promise<SignedCalendarSimulation> => {
  const response = await apiClient.get<SignedCalendarSimulationDto>(
    `${financingCompanyId}/loans/${id}/calendar-simulation`,
  )
  return transformSignedCalendarSimulationFromDto(response.data)
}

export function useGetCalendarSimulation(id: string, financingCompanyId: EFinancingCompany) {
  return useQuery({
    queryKey: loanQueryKeys.loanCalendar(id, financingCompanyId),
    queryFn: () => getCalendarSimulation(id, financingCompanyId),
  })
}

export function useRefetchCalendarSimulation(id: string, financingCompanyId: EFinancingCompany) {
  const queryClient = useQueryClient()

  return useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: loanQueryKeys.loanCalendar(id, financingCompanyId) })
  }, [queryClient, id, financingCompanyId])
}

const computeCalendarAction = async (
  id: string,
  financingCompanyId: EFinancingCompany,
  action: ComputeCalendarSimulationAction,
): Promise<SignedCalendarSimulation> => {
  const actionDto = transformComputeCalendarSimulationActionToDto(action)
  const response = await apiClient.post<SignedCalendarSimulationDto>(
    `${financingCompanyId}/loans/${id}/calendar-simulation/compute-action`,
    actionDto,
  )
  return transformSignedCalendarSimulationFromDto(response.data)
}

export function useComputeCalendarAction(id: string, financingCompanyId: EFinancingCompany) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: loanQueryKeys.computeAction(id),
    mutationFn: (action: ComputeCalendarSimulationAction) => computeCalendarAction(id, financingCompanyId, action),
    onSuccess: async (simulation) => {
      // Automatically acquire lock if simulation has an expiration date
      if (simulation.simulation.simulationExpiresOn) {
        try {
          const lockResult = await acquireLock(id, financingCompanyId)
          // Update lock state in cache
          queryClient.setQueryData(loanQueryKeys.loanLock(id, financingCompanyId), lockResult)
        } catch (error) {
          // Lock acquisition failure is not critical - user can continue with simulation
          console.warn('Failed to automatically acquire lock:', error)
        }
      }
    },
  })
}

const applyCalendarSimulation = async (
  id: string,
  financingCompanyId: EFinancingCompany,
  simulation: SignedCalendarSimulation,
): Promise<SignedCalendarSimulation> => {
  const simulationDto = transformSignedCalendarSimulationToDto(simulation)
  const response = await apiClient.post<SignedCalendarSimulationDto>(
    `${financingCompanyId}/loans/${id}/calendar-simulation/apply-simulation`,
    simulationDto,
  )
  return transformSignedCalendarSimulationFromDto(response.data)
}

// errorCode of the 409 the backend returns when the caller no longer holds the loan lock (ConflictException.LoanLockNotHeldErrorCode)
const LOAN_LOCK_NOT_HELD_ERROR_CODE = 'loanLockNotHeld'

export const isLoanLockNotHeldError = (error: unknown): boolean =>
  isAxiosError<{ errorCode?: string }>(error) &&
  error.response?.status === 409 &&
  error.response.data?.errorCode === LOAN_LOCK_NOT_HELD_ERROR_CODE

export function useApplyCalendarSimulation(id: string, financingCompanyId: EFinancingCompany) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: loanQueryKeys.applySimulation(id),
    mutationFn: (simulation: SignedCalendarSimulation) => applyCalendarSimulation(id, financingCompanyId, simulation),
    onError: (error) => {
      // The cached lock still says we hold it; refetch so the lock control shows the actual holder
      if (isLoanLockNotHeldError(error))
        void queryClient.invalidateQueries({ queryKey: loanQueryKeys.loanLock(id, financingCompanyId) })
    },
    onSuccess: async (data) => {
      queryClient.setQueryData(loanQueryKeys.loanCalendar(id, financingCompanyId), data)
      // The save rebooks the journals; the transactions tab must be refreshed.
      void queryClient.invalidateQueries({ queryKey: loanQueryKeys.journalTransactions(id, financingCompanyId) })

      // Automatically release lock after successful save
      try {
        const lockResult = await releaseLock(id, financingCompanyId)
        // Update lock state in cache
        queryClient.setQueryData(loanQueryKeys.loanLock(id, financingCompanyId), lockResult)
      } catch (error) {
        // Lock release failure is not critical - user can manually release if needed
        console.warn('Failed to automatically release lock:', error)
      }
    },
  })
}

const calculateAmountToSpread = async (
  id: string,
  financingCompanyId: EFinancingCompany,
  spreadOverdueAmount: SpreadOverdueAmount,
): Promise<SpreadOverdueAmountResultDto> => {
  const dto = transformSpreadOverdueAmountToDto(spreadOverdueAmount)
  const response = await apiClient.post<SpreadOverdueAmountResultDto>(
    `${financingCompanyId}/loans/${id}/calendar-simulation/calculate-amount-to-spread-over`,
    dto,
  )
  return response.data
}

export function useCalculateAmountToSpread(id: string, financingCompanyId: EFinancingCompany) {
  return useMutation({
    mutationKey: loanQueryKeys.computeAction(id),
    mutationFn: (spreadOverdueAmount: SpreadOverdueAmount) =>
      calculateAmountToSpread(id, financingCompanyId, spreadOverdueAmount),
  })
}

export interface ReplayCalendarActions {
  simulation: CalendarSimulation
  actions: CalendarSimulationAction[]
}

const replayCalendarActions = async (
  id: string,
  financingCompanyId: EFinancingCompany,
  { simulation, actions }: ReplayCalendarActions,
): Promise<SignedCalendarSimulation> => {
  const simulationDto = CalendarSimulation.toDto(simulation)
  const response = await apiClient.post<SignedCalendarSimulationDto>(
    `${financingCompanyId}/loans/${id}/calendar-simulation/refresh-simulation`,
    {
      signature: '',
      simulation: { ...simulationDto, actions: actions.map(transformCalendarActionToDto) },
    },
  )
  return transformSignedCalendarSimulationFromDto(response.data)
}

/**
 * Rebuilds the calendar by replaying `actions` in order against freshly fetched loan data.
 * The server reads nothing but the action list off the simulation — which is why the
 * signature goes up empty — so the same call serves both readings of "replay": all of the
 * actions, to recompute an expired simulation, or a prefix of them, to undo the rest.
 *
 * It throws when an action no longer computes against the current loan, leaving the caller's
 * stack untouched.
 */
export function useReplayCalendarActions(id: string, financingCompanyId: EFinancingCompany) {
  return useMutation({
    mutationKey: loanQueryKeys.refreshSimulation(id),
    mutationFn: (replay: ReplayCalendarActions) => replayCalendarActions(id, financingCompanyId, replay),
  })
}

const computeCatchUpAmountOfOverdueForDays = async (
  simulation: CalendarSimulation,
  startDate: DateOnly,
  endDate?: DateOnly | null,
): Promise<Map<string, number>> => {
  const simulationDto = CalendarSimulation.toDto(simulation)
  const response = await apiClient.post<{ date: string; amount: number }[]>(
    `${simulation.fullLoan.loan.financingCompanyId}/loans/${simulation.fullLoan.loan.id}/calendar-simulation/compute-catch-up-amount-of-overdue-for-days`,
    {
      simulation: simulationDto,
      startDate,
      endDate: endDate ?? null,
    },
  )
  return new Map(response.data.map((item) => [item.date, item.amount]))
}

export function useComputeCatchUpAmountOfOverdueForDays(
  simulation: CalendarSimulation,
  startDate: DateOnly,
  endDate?: DateOnly | null,
  enabled = true,
) {
  return useQuery<Map<string, number>>({
    queryKey: loanQueryKeys.computeCatchUpAmountOfOverdueForDays(simulation, startDate, endDate),
    queryFn: () => computeCatchUpAmountOfOverdueForDays(simulation, startDate, endDate),
    enabled,
  })
}

export interface CalculateNumberOfPaymentsToClearOverdueAmountResult {
  numberOfPayments: number
  lastPaymentAmount: number
}

const calculateNumberOfPaymentsToClearOverdueAmount = async (
  id: string,
  financingCompanyId: EFinancingCompany,
  simulation: SignedCalendarSimulation,
  fromPaymentDate: DateOnly,
  amountPerPayment: number,
): Promise<CalculateNumberOfPaymentsToClearOverdueAmountResult> => {
  const simulationDto = transformSignedCalendarSimulationToDto(simulation)
  const dto = {
    simulation: simulationDto.simulation,
    fromPaymentDate: fromPaymentDate.toISOString(),
    amountPerPayment,
  }
  const response = await apiClient.post<{ numberOfPayments: number; lastPaymentAmount: number }>(
    `${financingCompanyId}/loans/${id}/calendar-simulation/calculate-number-of-payments-to-clear-overdue-amount`,
    dto,
  )
  return {
    numberOfPayments: response.data.numberOfPayments,
    lastPaymentAmount: response.data.lastPaymentAmount,
  }
}

export function useCalculateNumberOfPaymentsToClearOverdueAmount(
  id: string,
  financingCompanyId: EFinancingCompany,
  simulation: SignedCalendarSimulation,
  fromPaymentDate: DateOnly,
  amountPerPayment: number,
  enabled = true,
) {
  return useQuery<CalculateNumberOfPaymentsToClearOverdueAmountResult>({
    queryKey: loanQueryKeys.calculateNumberOfPaymentsToClearOverdueAmount(
      id,
      financingCompanyId,
      simulation,
      fromPaymentDate,
      amountPerPayment,
    ),
    queryFn: () =>
      calculateNumberOfPaymentsToClearOverdueAmount(
        id,
        financingCompanyId,
        simulation,
        fromPaymentDate,
        amountPerPayment,
      ),
    enabled: enabled && !!id && !!fromPaymentDate && amountPerPayment > 0,
  })
}

const acquireLock = async (loanId: string, financingCompanyId: EFinancingCompany): Promise<LoanLockDto> => {
  const response = await apiClient.post<LoanLockDto>(`${financingCompanyId}/loans/${loanId}/reservation`)
  return response.data
}

export function useAcquireLock(loanId: string, financingCompanyId: EFinancingCompany) {
  return useMutation({
    mutationKey: loanQueryKeys.acquireLock(loanId),
    mutationFn: () => acquireLock(loanId, financingCompanyId),
  })
}

const releaseLock = async (loanId: string, financingCompanyId: EFinancingCompany): Promise<LoanLockDto> => {
  const response = await apiClient.delete<LoanLockDto>(`${financingCompanyId}/loans/${loanId}/reservation`)
  return response.data
}

export function useReleaseLock(loanId: string, financingCompanyId: EFinancingCompany) {
  return useMutation({
    mutationKey: loanQueryKeys.releaseLock(loanId),
    mutationFn: () => releaseLock(loanId, financingCompanyId),
  })
}

const getLock = async (loanId: string, financingCompanyId: EFinancingCompany): Promise<LoanLockDto> => {
  const response = await apiClient.get<LoanLockDto>(`${financingCompanyId}/loans/${loanId}/reservation`)
  return response.data
}

export function useGetLock(loanId: string, financingCompanyId: EFinancingCompany, enabled = true) {
  return useQuery({
    queryKey: loanQueryKeys.loanLock(loanId, financingCompanyId),
    queryFn: () => getLock(loanId, financingCompanyId),
    enabled: enabled && !!loanId && !!financingCompanyId,
  })
}

const replayMigrationEvents = async (loanId: string, financingCompanyId: EFinancingCompany): Promise<void> => {
  try {
    await apiClient.post(`${financingCompanyId}/loans/${loanId}/replay-migration-events`)
  } catch (error) {
    reportErrorToConsole(error)
    throw error
  }
}

export function useReplayMigrationEvents(loanId: string, financingCompanyId: EFinancingCompany) {
  return useMutation({
    mutationKey: loanQueryKeys.replayMigrationEvents(loanId),
    mutationFn: () => replayMigrationEvents(loanId, financingCompanyId),
  })
}

const generateValidationReport = async (loanId: string, financingCompanyId: EFinancingCompany): Promise<void> => {
  try {
    await apiClient.post(`${financingCompanyId}/loans/${loanId}/generate-validation-report`)
  } catch (error) {
    reportErrorToConsole(error)
    throw error
  }
}

export function useGenerateValidationReport(loanId: string, financingCompanyId: EFinancingCompany) {
  return useMutation({
    mutationKey: loanQueryKeys.generateValidationReport(loanId),
    mutationFn: () => generateValidationReport(loanId, financingCompanyId),
  })
}
